// ============================================================================
// BOOK APPOINTMENT MODAL — Shared Calendly-style booking form.
//
// Extracted from SchedulingView.jsx so both SchedulingView and WorkItemDrawer
// can import it without circular dependencies.
//
// Exports:
//   BookAppointmentModal  — full booking form (calendar, time, specialist, note)
//   CalendarPicker        — month calendar + time-slot picker sub-component
//   to24h / from24h       — time format converters
//   TIME_SLOTS / DAY_HEADERS — constants
//   fmtLabel / fmtVisitType / fmtVisitPurpose — formatters
//   (Status/outcome/priority badges now come from RGStatusBadge + statusConfig.)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Calendar, Loader2, AlertCircle, X,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY,
  getSchedulingAvailability, isDayAllowed, isTimeAllowed,
} from '../core/index.js';
import { setScheduled, getBookedSlots } from '../core/store.scheduling.js';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, query, where, getDocs,
} from 'firebase/firestore';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';

function getDb() { return getFirestore(getApp()); }

// ── Display config ─────────────────────────────────────────────────────────────
// Status/outcome/priority badge visuals are centralized in src/core/statusConfig.js
// and rendered via <RGStatusBadge>. No local status color maps live here.

// ── Constants ──────────────────────────────────────────────────────────────────

export const TIME_SLOTS = [
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM',
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM',
];

export const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Helpers ────────────────────────────────────────────────────────────────────

export function fmtLabel(str) {
  if (!str) return '—';
  return String(str).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const VISIT_TYPE_LABELS = {
  'in-person': 'In-Person',
  'video':     'Video (Telehealth)',
  'phone':     'Phone (Telehealth)',
  'async':     'Async Message',
};

const VISIT_PURPOSE_LABELS = {
  'NEW_PATIENT_INITIAL_CONSULT': 'New Patient / Initial Consult',
  'FOLLOW_UP':                   'Follow-up',
  'PROCEDURE_TESTING':           'Procedure / Testing',
  'SECOND_OPINION':              'Second Opinion',
  'URGENT_CONSULT':              'Urgent Consult',
};

export function fmtVisitType(val)    { return val ? (VISIT_TYPE_LABELS[val]    ?? fmtLabel(val)) : null; }
export function fmtVisitPurpose(val) { return val ? (VISIT_PURPOSE_LABELS[val] ?? fmtLabel(val)) : null; }

/** Convert "9:00 AM" → "09:00" (24h) for storing in Firestore. */
export function to24h(displayTime) {
  if (!displayTime) return null;
  const [timePart, period] = displayTime.split(' ');
  let [h, m] = timePart.split(':').map(Number);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Convert "09:00" (24h stored) → "9:00 AM" for display. */
export function from24h(time24) {
  if (!time24) return null;
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr ?? '00';
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
}

// ── Badge ──────────────────────────────────────────────────────────────────────


// ── Calendly-style Date + Time Picker ─────────────────────────────────────────

export function CalendarPicker({ selectedDate, onSelectDate, selectedTime, onSelectTime, takenSlots = new Set(), slotsLoading = false, slotsError = null, onRetrySlots, availability = null, currentAppointment = null }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const firstDow  = new Date(year, month, 1).getDay();
  const startPad  = firstDow === 0 ? 6 : firstDow - 1;
  const daysTotal = new Date(year, month + 1, 0).getDate();

  const cells = [
    ...Array(startPad).fill(null),
    ...Array.from({ length: daysTotal }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const isBefore = (day) => {
    if (!day) return false;
    return new Date(year, month, day) < today;
  };

  // Scheduling-availability helpers. When `availability` is null, no day/time
  // constraint is applied (preserves behavior for callers that omit it). The
  // current appointment's own date/time is always exempt so a legacy appointment
  // outside availability stays visible and selectable for reschedule.
  const dateStrOf = (day) =>
    day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;

  const isUnavailableDay = (day) => {
    if (!day || !availability) return false;
    const ds = dateStrOf(day);
    if (currentAppointment?.date && ds === currentAppointment.date) return false;
    return !isDayAllowed(availability, ds);
  };

  const isUnavailableSlot = (slot) => {
    if (!availability) return false;
    const t = to24h(slot);
    if (currentAppointment?.date === selectedDate && currentAppointment?.time === t) return false;
    return !isTimeAllowed(availability, t);
  };

  const isSelectedDay = (day) => {
    if (!day || !selectedDate) return false;
    const target = new Date(selectedDate + 'T00:00:00');
    return (
      target.getFullYear() === year &&
      target.getMonth()    === month &&
      target.getDate()     === day
    );
  };

  const isTodayDay = (day) => {
    if (!day) return false;
    const d = new Date(year, month, day);
    return d.toDateString() === today.toDateString();
  };

  const handleDayClick = (day) => {
    if (!day || isBefore(day) || isUnavailableDay(day)) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onSelectDate(dateStr);
    onSelectTime('');
  };

  const canPrevMonth = () => {
    const prev = new Date(year, month - 1, 1);
    const todayFirst = new Date(today.getFullYear(), today.getMonth(), 1);
    return prev >= todayFirst;
  };

  const selectedDayLabel = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <div className="flex gap-5">

      {/* Calendar pane */}
      <div className="flex-1 min-w-0">

        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => canPrevMonth() && setViewDate(new Date(year, month - 1, 1))}
            disabled={!canPrevMonth()}
            className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-white">{monthLabel}</span>
          <button
            type="button"
            onClick={() => setViewDate(new Date(year, month + 1, 1))}
            className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="text-center text-[10px] text-slate-500 font-semibold uppercase tracking-wide py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((day, i) => {
            const past     = isBefore(day);
            const unavailable = isUnavailableDay(day);
            const blocked  = past || unavailable;
            const selected = isSelectedDay(day);
            const todayMark = isTodayDay(day);
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleDayClick(day)}
                disabled={!day || blocked}
                title={unavailable && !past ? 'Outside scheduling availability' : undefined}
                className={[
                  'aspect-square rounded-full text-xs font-medium transition-all flex items-center justify-center',
                  !day         ? 'invisible' : '',
                  blocked      ? 'text-slate-700 cursor-not-allowed' : '',
                  selected     ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : '',
                  !selected && todayMark && !blocked
                               ? 'border border-indigo-500/60 text-indigo-300' : '',
                  !selected && !todayMark && !blocked
                               ? 'text-slate-300 hover:bg-slate-700/60 hover:text-white' : '',
                ].join(' ')}
              >
                {day ?? ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* Vertical divider (only when date is selected) */}
      {selectedDate && (
        <div className="w-px bg-slate-700/50 self-stretch flex-shrink-0" />
      )}

      {/* Time slots pane */}
      {selectedDate && (
        <div className="w-32 flex flex-col flex-shrink-0">
          <div className="mb-3 text-center">
            <p className="text-xs font-semibold text-white leading-snug">
              {selectedDayLabel?.split(', ')[0]}
            </p>
            <p className="text-[11px] text-slate-400 leading-snug">
              {selectedDayLabel?.split(', ').slice(1).join(', ')}
            </p>
          </div>
          <div className="overflow-y-auto space-y-1.5 flex-1 max-h-56 pr-0.5 scrollbar-thin">
            {slotsLoading ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />
                <p className="text-[11px] text-slate-500 italic">Loading availability…</p>
              </div>
            ) : slotsError ? (
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <p className="text-[11px] text-red-400">Could not load availability.</p>
                {onRetrySlots && (
                  <button
                    type="button"
                    onClick={onRetrySlots}
                    className="text-[11px] text-indigo-400 underline underline-offset-2 hover:text-indigo-300"
                  >
                    Retry
                  </button>
                )}
              </div>
            ) : (
              <>
                {TIME_SLOTS.filter((slot) => !takenSlots.has(to24h(slot))).map((slot) => {
                  const isActive = selectedTime === slot;
                  const unavailable = isUnavailableSlot(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => { if (!unavailable) onSelectTime(isActive ? '' : slot); }}
                      disabled={unavailable}
                      title={unavailable ? 'Outside scheduling availability' : undefined}
                      className={[
                        'w-full rounded-lg text-xs font-medium border transition-all py-2',
                        unavailable
                          ? 'bg-slate-900/40 border-slate-800/40 text-slate-700 cursor-not-allowed'
                          : isActive
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                            : 'bg-slate-800/50 border-slate-700/40 text-slate-300 hover:bg-indigo-600/20 hover:border-indigo-500/40 hover:text-indigo-300',
                      ].join(' ')}
                    >
                      {slot}
                    </button>
                  );
                })}
                {TIME_SLOTS.filter((slot) => !takenSlots.has(to24h(slot))).every((slot) => isUnavailableSlot(slot)) && (
                  <p className="text-[11px] text-slate-600 text-center py-4 italic">
                    No available times for this date.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Book Appointment Modal ────────────────────────────────────────────────────

export default function BookAppointmentModal({ item, uid, role, onClose, onBooked, scheduledItems = [] }) {
  const { referral, workflow } = item;
  const tenantId   = referral?.tenantId;
  const referralId = referral?.id;
  const specialtyId = referral?.specialtyId ?? null;
  const priority   = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const outcome    = referral?.finalOutcome ?? referral?.outcome?.status ?? null;

  // Date + time (Calendly picker)
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Optional details
  const [consultantId,   setConsultantId]   = useState('');
  const [schedulingNote, setSchedulingNote] = useState('');

  // Specialists for this specialty
  const [specialists,    setSpecialists]    = useState([]);
  const [specsLoading,   setSpecsLoading]   = useState(true);

  // Write state
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Booked-slot availability (independent query) ──────────────────────────
  const [takenSlots,   setTakenSlots]   = useState(new Set());
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError,   setSlotsError]   = useState(null);

  const fetchSlots = useCallback(async () => {
    if (!tenantId || !consultantId || !selectedDate) {
      setTakenSlots(new Set());
      setSlotsError(null);
      return;
    }
    setSlotsLoading(true);
    setSlotsError(null);
    try {
      const booked = await getBookedSlots(tenantId, consultantId, selectedDate, referralId);
      setTakenSlots(booked);
    } catch {
      setTakenSlots(new Set());
      setSlotsError('Failed to load specialist availability.');
    } finally {
      setSlotsLoading(false);
    }
  }, [tenantId, consultantId, selectedDate, referralId]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // If the currently selected time becomes taken, clear it.
  useEffect(() => {
    if (selectedTime && takenSlots.has(to24h(selectedTime))) {
      setSelectedTime('');
    }
  }, [takenSlots, selectedTime]);

  // ── Scheduling Availability (booking constraint source of truth) ───────────
  const [availability,      setAvailability]      = useState(null);
  const [availabilityState, setAvailabilityState] = useState('loading'); // loading | ready | missing | error

  const loadAvailability = useCallback(async () => {
    if (!tenantId) { setAvailabilityState('error'); return; }
    setAvailabilityState('loading');
    try {
      const { exists, availability: a } = await getSchedulingAvailability(tenantId);
      if (!exists) { setAvailability(null); setAvailabilityState('missing'); }
      else { setAvailability(a); setAvailabilityState('ready'); }
    } catch {
      setAvailability(null);
      setAvailabilityState('error');
    }
  }, [tenantId]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  // Fetch specialists who belong to this specialty
  useEffect(() => {
    if (!tenantId) { setSpecsLoading(false); return; }
    setSpecsLoading(true);
    const usersRef = collection(getDb(), 'users');
    const constraints = [where('tenantId', '==', tenantId)];
    if (specialtyId) {
      constraints.push(where('specialtyIds', 'array-contains', specialtyId));
    }
    getDocs(query(usersRef, ...constraints))
      .then((snap) => {
        const results = [];
        snap.forEach((d) => {
          const data = d.data();
          if (data.role !== 'specialist') return;
          const name = data.displayName || data.name || data.email || d.id;
          results.push({ uid: d.id, name });
        });
        results.sort((a, b) => a.name.localeCompare(b.name));
        setSpecialists(results);
      })
      .catch(() => setSpecialists([]))
      .finally(() => setSpecsLoading(false));
  }, [tenantId, specialtyId]);

  const canBook = !!selectedDate && !!selectedTime && !!consultantId && !slotsLoading && !slotsError
    && availabilityState === 'ready'
    && isDayAllowed(availability, selectedDate)
    && isTimeAllowed(availability, to24h(selectedTime));

  const handleBook = async () => {
    if (!selectedDate) {
      setSaveError('Select an appointment date to continue.');
      return;
    }
    if (!selectedTime) {
      setSaveError('Select an appointment time to continue.');
      return;
    }
    if (!consultantId) {
      setSaveError('Select a specialist to continue.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const selectedSpec = specialists.find((s) => s.uid === consultantId) ?? null;
      if (!selectedSpec?.uid) {
        setSaveError('Selected specialist could not be resolved. Please re-select and retry.');
        setSaving(false);
        return;
      }

      // ── Collision re-check immediately before write ──────────────────────
      const freshBooked = await getBookedSlots(tenantId, consultantId, selectedDate, referralId);
      const requestedTime = to24h(selectedTime);
      if (freshBooked.has(requestedTime)) {
        setTakenSlots(freshBooked);
        setSelectedTime('');
        setSaveError('This time slot was just booked by another scheduler. Please choose a different time.');
        setSaving(false);
        return;
      }

      const details = {
        appointmentDate:  selectedDate,
        appointmentTime:  requestedTime,
        consultantId:     selectedSpec.uid,
        consultantName:   selectedSpec.name,
        locationId:       null,
        schedulingNote:   schedulingNote.trim() || null,
      };
      await setScheduled(tenantId, referralId, workflow, details, uid, role);
      onBooked(referralId, details);
      onClose();
    } catch (err) {
      setSaveError(safeUserError(err) ?? 'Failed to book appointment. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/50';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">

          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-slate-800/50 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-white">Book Appointment</h2>
              <p className="text-sm text-slate-400 mt-0.5">This referral is ready to schedule.</p>
            </div>
            <button
              type="button"
              onClick={() => !saving && onClose()}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Context strip */}
          <div className="px-6 py-3 bg-slate-800/40 border-b border-slate-800/50 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <RGStatusBadge type="workflow" status={WORKFLOW_STATUS.READY_TO_SCHEDULE} />
              {outcome && <RGStatusBadge type="outcome" status={outcome} />}
              {priority !== WORKFLOW_PRIORITY.NORMAL && (
                <RGStatusBadge type="priority" status={priority} />
              )}
            </div>
            <p className="text-sm font-medium text-white">
              {fmtLabel(referral?.specialtyId)}
              {referral?.packId && (
                <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>
              )}
            </p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{referralId ?? '—'}</p>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">

            {/* Visit Information */}
            <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Visit Information
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-1">Visit Type</p>
                  <p className="text-sm text-slate-300">
                    {fmtVisitType(referral?.visitType) || <span className="text-slate-600 italic">—</span>}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-1">Reason for Escalation</p>
                  <p className="text-sm text-slate-300">
                    {referral?.reasonForEscalation || <span className="text-slate-600 italic">—</span>}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-1">Visit Purpose</p>
                  <p className="text-sm text-slate-300">
                    {fmtVisitPurpose(referral?.visitPurpose) || <span className="text-slate-600 italic">—</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Specialist */}
            <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Specialist{' '}
                <span className="normal-case font-normal text-slate-600">(optional)</span>
              </label>
              {specsLoading ? (
                <div className={`${inputCls} flex items-center gap-2 text-slate-500`}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Loading…</span>
                </div>
              ) : (
                <select
                  value={consultantId}
                  onChange={(e) => setConsultantId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Select specialist —</option>
                  {specialists.map(({ uid: sUid, name }) => (
                    <option key={sUid} value={sUid}>{name}</option>
                  ))}
                  {specialists.length === 0 && (
                    <option value="" disabled>No specialists found</option>
                  )}
                </select>
              )}
            </div>

            {/* Calendar + time slots */}
            <div className="px-6 pt-5 pb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">
                Select a Date &amp; Time
              </p>

              {availabilityState === 'loading' && (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading scheduling availability…
                </div>
              )}

              {availabilityState === 'missing' && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-300">
                    Scheduling availability has not been set up for your organization. A tenant admin must configure it in Impact Settings → Scheduling Availability before appointments can be booked.
                  </p>
                </div>
              )}

              {availabilityState === 'error' && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-red-300">
                    Could not load scheduling availability. Booking is blocked until it loads.{' '}
                    <button type="button" onClick={loadAvailability} className="underline underline-offset-2 hover:text-red-200">
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {availabilityState === 'ready' && (
                <CalendarPicker
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                  selectedTime={selectedTime}
                  onSelectTime={setSelectedTime}
                  takenSlots={takenSlots}
                  slotsLoading={slotsLoading}
                  slotsError={slotsError}
                  onRetrySlots={fetchSlots}
                  availability={availability}
                />
              )}
            </div>

            {/* Selected summary pill */}
            {selectedDate && (
              <div className="px-6 pb-4">
                <div className={[
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium',
                  selectedTime
                    ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300'
                    : 'bg-slate-700/40 border-slate-600/30 text-slate-400',
                ].join(' ')}>
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                  })}
                  {selectedTime && (
                    <><span className="text-slate-500 mx-0.5">·</span>{selectedTime}</>
                  )}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-slate-800/50 mx-6" />

            {/* Internal Note */}
            <div className="px-6 py-4">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Internal Note{' '}
                <span className="normal-case font-normal text-slate-600">(optional · non-PHI)</span>
              </label>
              <input
                type="text"
                value={schedulingNote}
                onChange={(e) => setSchedulingNote(e.target.value)}
                placeholder="Scheduling note…"
                className={inputCls}
              />
            </div>

            {/* Error */}
            {saveError && (
              <div className="mx-6 mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{saveError}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800/50 flex-shrink-0">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => !saving && onClose()}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBook}
                disabled={saving || !canBook}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-medium transition-colors"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</>
                  : <><Calendar className="w-4 h-4" /> Book Appointment</>}
              </button>
            </div>
            {!canBook && !saving && (
              <div className="flex items-center gap-3 mt-2.5 justify-center">
                <span className={`text-[11px] flex items-center gap-1 ${selectedDate ? 'text-emerald-500' : 'text-slate-600'}`}>
                  <span>{selectedDate ? '✓' : '○'}</span> Date
                </span>
                <span className="text-slate-700 text-[10px]">·</span>
                <span className={`text-[11px] flex items-center gap-1 ${selectedTime ? 'text-emerald-500' : 'text-slate-600'}`}>
                  <span>{selectedTime ? '✓' : '○'}</span> Time
                </span>
                <span className="text-slate-700 text-[10px]">·</span>
                <span className={`text-[11px] flex items-center gap-1 ${consultantId ? 'text-emerald-500' : 'text-slate-600'}`}>
                  <span>{consultantId ? '✓' : '○'}</span> Specialist
                </span>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
